import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Signature } from "lucide-react";
import ModalShell from "../modal/ModalShell";
import {
  dataUrlToBase64,
  exportSignaturePng,
  hasInk,
  paintStrokes,
  SIGNATURE_ASPECT,
  SignatureStroke,
} from "../../features/training/utils/signatureStrokes";

interface SignatureSheetProps {
  /** What is being signed, e.g. the programme's name. */
  subject: string;
  /** Receives the PNG as base64; resolves false when the save failed (the sheet stays open). */
  onSign: (imagePngBase64: string) => Promise<boolean>;
  onClose: () => void;
}

const LIGHT_INK = "#1a1a1a";
const DARK_INK = "#f2f2f2";

const screenInk = (): string =>
  document.documentElement.classList.contains("dark") ? DARK_INK : LIGHT_INK;

/**
 * The sheet the member signs on, handed over on the school's screen: a strip of paper that
 * takes finger, pen or mouse strokes (pointer events, coalesced samples), painted the way
 * the flashside drawing sheet paints them. "Sign" flattens the ink to a transparent PNG
 * cropped to the signature; the strokes themselves are never kept.
 */
const SignatureSheet: React.FC<SignatureSheetProps> = ({ subject, onSign, onClose }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const hintId = useId();
  const paperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<{ pointerId: number; stroke: SignatureStroke } | null>(null);
  const frame = useRef<number | null>(null);
  const strokesRef = useRef<SignatureStroke[]>([]);
  const [strokes, setStrokes] = useState<SignatureStroke[]>([]);
  const [paper, setPaper] = useState({ width: 0, height: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const screenScale = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;

  useEffect(() => {
    const element = paperRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      setPaper({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const repaint = useCallback((): void => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    paintStrokes(context, strokesRef.current, live.current?.stroke ?? null, screenInk(), screenScale);
  }, [screenScale]);

  const schedule = useCallback((): void => {
    if (frame.current === null) frame.current = requestAnimationFrame(repaint);
  }, [repaint]);

  useEffect(() => {
    strokesRef.current = strokes;
    repaint();
  }, [strokes, repaint]);

  // A resize clears the canvas, so it is sized and repainted with the paper.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = Math.round(paper.width * screenScale);
    canvas.height = Math.round(paper.height * screenScale);
    repaint();
  }, [paper, screenScale, repaint]);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    []
  );

  const pointOf = (clientX: number, clientY: number, pressure: number, type: string) => {
    const box = canvasRef.current!.getBoundingClientRect();
    return { x: clientX - box.left, y: clientY - box.top, pressure: type === "pen" ? pressure : null };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (live.current || (event.pointerType === "mouse" && event.button !== 0)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    live.current = {
      pointerId: event.pointerId,
      stroke: [pointOf(event.clientX, event.clientY, event.pressure, event.pointerType)],
    };
    schedule();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const current = live.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const samples = event.nativeEvent.getCoalescedEvents?.() ?? [];
    const stroke = [...current.stroke];
    for (const sample of samples.length ? samples : [event.nativeEvent]) {
      stroke.push(pointOf(sample.clientX, sample.clientY, sample.pressure, sample.pointerType));
    }
    live.current = { ...current, stroke };
    schedule();
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const current = live.current;
    if (!current || current.pointerId !== event.pointerId) return;
    live.current = null;
    setStrokes((existing) => [...existing, current.stroke]);
  };

  const sign = async (): Promise<void> => {
    if (!hasInk(strokes) || paper.width === 0) return;
    setBusy(true);
    setError(null);
    const dataUrl = exportSignaturePng(strokes, paper.width, paper.height, screenScale);
    const saved = dataUrl ? await onSign(dataUrlToBase64(dataUrl)) : false;
    setBusy(false);
    if (!saved) setError(t("training.signature.failed"));
  };

  return (
    <ModalShell onClose={onClose} labelledBy={titleId} describedBy={hintId} maxWidthClassName="max-w-2xl">
      <div className="p-4 lg:p-6">
        <h3 id={titleId} className="text-grey-10 dark:text-grey-90">
          {t("training.signature.title")}
        </h3>
        <p id={hintId} className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t("training.signature.hint", { programme: subject })}
        </p>

        <div
          ref={paperRef}
          className="with-border relative mt-4 w-full overflow-hidden rounded-2xl bg-white dark:bg-primary-20"
          style={{ aspectRatio: SIGNATURE_ASPECT }}
        >
          <div className="pointer-events-none absolute inset-x-6 bottom-8 border-b border-grey-70 dark:border-grey-40" />
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={t("training.signature.area")}
            data-testid="signature-canvas"
            className="absolute inset-0 h-full w-full cursor-crosshair touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
          />
        </div>

        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setStrokes([])}
            disabled={busy || strokes.length === 0}
          >
            {t("training.signature.clear")}
          </button>
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
              {t("training.signature.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={sign}
              disabled={busy || !hasInk(strokes)}
            >
              <Signature size={18} />
              <span>{t("training.signature.sign")}</span>
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default SignatureSheet;
